import axios from "axios";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "./firebase";

/**
 * SiliconLogix SMTP Welcome Email Service (Client-Side Dispatcher)
 * Ensures first-time signups receive their personalized welcome letter & Silicon ID.
 */

// Default local SMTP service endpoint, can be overridden via .env
const SMTP_SERVER_URL =
  process.env.REACT_APP_SMTP_SERVER_URL || "http://localhost:5001";

/**
 * Dispatches the welcome email via the dedicated SMTP microservice.
 * Enforces strict idempotency: will NEVER send if welcomeEmailSent is already true.
 *
 * @param {Object} params
 * @param {string} params.uid - User UID
 * @param {string} params.email - User email address
 * @param {string} params.name - User display name / full name
 * @param {string} params.siliconId - Assigned unique Silicon ID (e.g. SLX26-8941203)
 * @param {string} [params.role] - Account role (Student, Professional, etc.)
 * @param {boolean} [params.alreadySent] - Existing flag status
 * @returns {Promise<{ success: boolean, skipped?: boolean, error?: string }>}
 */
export const dispatchWelcomeEmail = async ({
  uid,
  email,
  name,
  siliconId,
  role = "Student",
  alreadySent = false,
}) => {
  // 1. Strict idempotency guard: only send for first-time signups
  if (alreadySent === true) {
    console.log(`[EmailService] Welcome email already sent for UID: ${uid}. Skipping.`);
    return { success: true, skipped: true };
  }

  if (!email || !siliconId) {
    console.warn("[EmailService] Missing email or siliconId, skipping welcome email.");
    return { success: false, error: "Missing email or siliconId" };
  }

  try {
    console.log(`[EmailService] Dispatching welcome email for ${email} with Silicon ID ${siliconId}...`);

    // Call the dedicated Node.js Nodemailer SMTP endpoint
    const response = await axios.post(
      `${SMTP_SERVER_URL}/api/send-welcome-email`,
      {
        email,
        name: name || email.split("@")[0],
        siliconId,
        role,
      },
      {
        headers: { "Content-Type": "application/json" },
        timeout: 8000, // 8-second timeout so user signup never hangs
      }
    );

    // 2. Mark Firestore document so it is NEVER sent again
    if (uid && db) {
      try {
        const userDocRef = doc(db, "users", uid);
        await updateDoc(userDocRef, {
          welcomeEmailSent: true,
          welcomeEmailSentAt: new Date(),
        });
        console.log(`✅ [EmailService] Marked welcomeEmailSent: true for ${uid}`);
      } catch (dbErr) {
        console.warn("[EmailService] Warning updating welcomeEmailSent in Firestore:", dbErr);
      }
    }

    return { success: true, data: response.data };
  } catch (error) {
    // Graceful error logging so auth signup flow proceeds smoothly even if SMTP server is offline
    console.warn(
      `⚠️ [EmailService] SMTP endpoint unreachable or returned an error: ${error.message}. Signup flow continued.`
    );

    // Fallback: Still mark in Firestore to prevent repeated attempts if desired
    if (uid && db) {
      try {
        const userDocRef = doc(db, "users", uid);
        await updateDoc(userDocRef, {
          welcomeEmailSent: true,
          welcomeEmailSentAt: new Date(),
          welcomeEmailError: error.message,
        });
      } catch (e) {}
    }

    return { success: false, error: error.message };
  }
};
